// src/App.jsx
import LogRocket from 'logrocket';
LogRocket.init('wjsrsf/besc-llc');

import React, { useState, useEffect, createContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WagmiConfig } from 'wagmi';
import { config } from './wagmi-config';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { Routes, Route, useLocation, Link } from 'react-router-dom';

import BridgeWidget from './components/BridgeWidget';
import CrossPort from './components/CrossPort';
import Referrals from './components/Referrals';

import './App.css';
import '@solana/wallet-adapter-react-ui/styles.css';

import PoweredByBESC from './assets/PoweredByBESC.png';
import AppLauncherIcon from './assets/app_launcher_icon.png';

// Validation helpers
const isValidEvmAddress = (address) => /^0x[a-fA-F0-9]{40}$/.test(address);
const isValidSolanaAddress = (address) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);

// Referral Context
export const ReferralContext = createContext({
  referrer: null,
  setReferrer: () => {},
});

export default function App() {
  const endpoint = (
    process.env.REACT_APP_SOLANA_RPC_URL ||
    process.env.REACT_APP_SOLANA_RPC_ENDPOINTS?.split(',')?.[0]?.trim() ||
    'https://rpc.ankr.com/solana'
  );
  const network = WalletAdapterNetwork.Mainnet;
  const wallets = useMemo(() => [new PhantomWalletAdapter()], []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExternalLinksOpen, setIsExternalLinksOpen] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      console.log('Toggling menu, new state:', !prev);
      return !prev;
    });
    if (isMenuOpen) {
      // If we just closed the main menu, also close External Links
      setIsExternalLinksOpen(false);
    }
  };

  const toggleExternalLinks = () => {
    setIsExternalLinksOpen((prev) => !prev);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const ref = urlParams.get('ref');
    if (ref && (isValidEvmAddress(ref) || isValidSolanaAddress(ref))) {
      localStorage.setItem('referral', ref);
      setReferrer(ref);
    } else {
      const storedRef = localStorage.getItem('referral');
      if (
        storedRef &&
        (isValidEvmAddress(storedRef) || isValidSolanaAddress(storedRef))
      ) {
        setReferrer(storedRef);
      } else {
        setReferrer(null);
      }
    }
  }, [location]);

  const navLinks = [
    { label: 'BESC Bridge', url: '/besc-bridge' },
    { label: 'BESC CrossPort', url: '/crossport' },
    { label: 'Referrals', url: '/referrals' },
  ];

  const externalLinks = [
    { label: 'Website', url: 'https://bescfinancial.com' },
    { label: 'Swap', url: 'https://bescswap.com' },
    { label: 'Support', url: 'https://t.me/buildersedgeportal' },
    { label: 'Twitter', url: 'https://twitter.com/bescllc' },
  ];

  const heroStats = [
    {
      value: '30+',
      label: 'Chains',
      detail: 'Connected via CrossPort routing',
    },
    {
      value: '2372',
      label: 'BESC HyperChain ID',
      detail: 'Chain identifier for the BESC mainnet',
    },
    {
      value: '24/7',
      label: 'Monitoring',
      detail: 'Auto-scaling + live support',
    },
  ];

  const heroFlows = [
    {
      title: 'USDC → BUSDC',
      detail:
        'Mint BUSDC inside the central pool, keeping every dollar fully collateralized before it hits the HyperChain.',
    },
    {
      title: 'BUSDC → USDC',
      detail:
        'Redeem BUSDC with instant settlement and the same on-chain approvals you already set in the bridge widget.',
    },
  ];

  const bridgeProducts = [
    {
      tag: 'Mint BUSDC',
      title: 'BESC Bridge',
      badge: 'Recommended',
      description:
        'A dedicated USDC bridge that mints BUSDC on the BESC HyperChain. Fully backed, fast settlement, and clear fees.',
      highlights: [
        'Funds settle in seconds, not minutes',
        'No hidden spreads — fees shown before you bridge',
      ],
      ctaLabel: 'Open BESC Bridge',
      ctaUrl: '/besc-bridge',
      ctaStyle: 'primary',
      variant: 'primary',
    },
    {
      tag: 'Multi-chain routing',
      title: 'BESC CrossPort',
      description:
        'Advanced router for 30+ chains and thousands of tokens. CrossPort finds the cheapest route across major bridges.',
      highlights: [
        'Cheapest route selection across networks',
        'Access 1,000s of tokens on demand',
        'Designed for power users and aggregators',
      ],
      ctaLabel: 'Launch CrossPort',
      ctaUrl: '/crossport',
      ctaStyle: 'ghost',
      variant: 'secondary',
    },
  ];

  const howItWorksSteps = [
    {
      title: 'Deposit USDC',
      detail: 'Send USDC from any supported chain.',
    },
    {
      title: 'BUSDC mint',
      detail: 'Minted on the HyperChain.',
    },
    {
      title: 'Redeem anytime',
      detail: 'Bridge out to USDC on supported networks.',
    },
  ];

  const walletChips = ['MetaMask', 'Phantom', 'WalletConnect', 'Ledger Live'];

  const BridgeExperience = () => (
    <>
      <section className="hero">
        <div className="hero__content">
          <p className="hero__eyebrow">BESC Bridge Command Center</p>
          <h1>USDC ↔ BUSDC. Fully backed. Transparent fees. Instant settlement.</h1>
          <p className="hero__description">
            Mint and redeem BUSDC using centralized USDC liquidity, secured by the BESC HyperChain and
            routed across 30+ networks.
          </p>
          <div className="hero__product-grid">
            {bridgeProducts.map((product) => (
              <article
                key={product.title}
                className={`hero__product-card hero__product-card--${product.variant}`}
              >
                {product.badge && (
                  <span className="hero__product-badge">{product.badge}</span>
                )}
                <p className="hero__product-tag">{product.tag}</p>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <ul className="hero__product-list">
                  {product.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
                <Link
                  to={product.ctaUrl}
                  className={`button ${product.ctaStyle} hero__product-cta`}
                  aria-label={product.ctaLabel}
                >
                  {product.ctaLabel}
                </Link>
              </article>
            ))}
          </div>
          <div className="hero__stats">
            {heroStats.map((stat) => (
              <div key={stat.label} className="hero__stat-card">
                <div className="hero__stat-value">{stat.value}</div>
                <div className="hero__stat-label">{stat.label}</div>
                <div className="hero__stat-detail">{stat.detail}</div>
              </div>
            ))}
          </div>

          <div className="hero__how">
            <div className="hero__how-title">How BESC Bridge Works</div>
            <div className="hero__how-steps">
              {howItWorksSteps.map((step, index) => (
                <div key={step.title} className="hero__how-step">
                  <span className="hero__how-index">{index + 1}</span>
                  <div>
                    <div className="hero__how-step-title">{step.title}</div>
                    <p className="hero__how-step-detail">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero__flow">
            {heroFlows.map((flow) => (
              <div key={flow.title} className="hero__flow-card">
                <div className="hero__flow-title">{flow.title}</div>
                <p className="hero__flow-detail">{flow.detail}</p>
              </div>
            ))}
          </div>

          <div className="hero__wallet-focus">
            <p className="hero__wallet-title">Wallet connect</p>
            <p>
              Connect MetaMask, Phantom, WalletConnect, and Ledger straight from the BESC Bridge widget to
              keep private keys in the same place that mints and redeems BUSDC.
            </p>
            <div className="hero__wallet-chips">
              {walletChips.map((chip) => (
                <span key={chip}>{chip}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="referrals-teaser">
        <div className="referrals-teaser__content">
          <p className="referrals-teaser__eyebrow">Referrals</p>
          <h2>Referral rewards are coming soon.</h2>
          <p>
            Share your link and earn from users who bridge with BESC Bridge or CrossPort.
            A tracked dashboard and payouts are on the way.
          </p>
        </div>
        <div className="referrals-teaser__actions">
          <Link to="/referrals" className="button ghost" aria-label="View referrals page">
            View Referrals
          </Link>
        </div>
      </section>

    </>
  );

  const BridgePage = () => (
    <section className="bridge-page">
      <div className="bridge-page__intro">
        <p className="hero__eyebrow">BESC Bridge Widget</p>
        <h2>One Widget</h2>
        <p>
          The dedicated bridge page lets you flow USDC to BUSDC (and back) with clear fees, pool limits, and
          wallet approvals. This is where the command center’s approvals turn into on-chain minting.
        </p>
      </div>
      <div className="bridge-page__widget">
        <BridgeWidget />
      </div>
    </section>
  );

  return (
    <WagmiConfig config={config}>
      <ConnectionProvider endpoint={endpoint} config={{ commitment: 'confirmed' }}>
        <WalletProvider wallets={wallets} autoConnect={true}>
          <WalletModalProvider>
            <ReferralContext.Provider value={{ referrer, setReferrer }}>
              <div className="app-container">
                <img
                  src="https://usagif.com/wp-content/uploads/gif/outerspace-18.gif"
                  alt="Galaxy Background"
                  className="bg-gif"
                  onError={(e) => (e.target.src = '/fallback-galaxy.jpg')}
                  loading="lazy"
                />

                <div className="overlay">
                  <motion.header
                    className="header"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    role="banner"
                  >
                    <Link to="/" className="header__brand">
                      <img
                        src={AppLauncherIcon}
                        alt="BESC Logo"
                        className="header-logo"
                      />
                      <div className="header__brand-text">
                        <span className="header__brand-title">BESC Bridge</span>
                        <span className="header__brand-subtitle">
                          HyperChain + CrossPort
                        </span>
                      </div>
                    </Link>

                    <nav className="nav-links" aria-label="Main navigation">
                      {navLinks.map(({ label, url }) => (
                        <Link
                          key={label}
                          to={url}
                          className="nav-link"
                          aria-label={label}
                        >
                          {label}
                        </Link>
                      ))}
                    </nav>

                    <button
                      className="hamburger"
                      onClick={toggleMenu}
                      aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                      aria-expanded={isMenuOpen}
                    >
                      <span className="hamburger-icon"></span>
                    </button>
                  </motion.header>

                  <AnimatePresence>
                    {isMenuOpen && (
                      <motion.div
                        key="mobile-menu"
                        className="mobile-menu open"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        role="dialog"
                        aria-label="Mobile navigation"
                        onAnimationComplete={() =>
                          console.log('Mobile menu animation complete')
                        }
                      >
                        <button
                          className="close-menu"
                          onClick={toggleMenu}
                          aria-label="Close menu"
                        >
                          ✕
                        </button>

                        <nav aria-label="Mobile navigation">
                          {console.log('Rendering navLinks:', navLinks)}
                          {navLinks.map(({ label, url }) => (
                            <Link
                              key={label}
                              to={url}
                              className="nav-link"
                              onClick={toggleMenu}
                              aria-label={label}
                            >
                              {label}
                            </Link>
                          ))}

                          <button
                            className="external-links-toggle"
                            onClick={toggleExternalLinks}
                            aria-expanded={isExternalLinksOpen}
                            aria-label={
                              isExternalLinksOpen
                                ? 'Collapse external links'
                                : 'Expand external links'
                            }
                          >
                            External Links {isExternalLinksOpen ? '▲' : '▼'}
                          </button>

                          <AnimatePresence>
                            {isExternalLinksOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="external-links"
                              >
                                {externalLinks.map(({ label, url }) => (
                                  <a
                                    key={label}
                                    href={url}
                                    className="nav-link external"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={toggleMenu}
                                    aria-label={label}
                                  >
                                    {label}
                                  </a>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </nav>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <main className="main">
                    <Routes>
                      <Route path="/crossport" element={<CrossPort />} />
                      <Route path="/besc-bridge" element={<BridgePage />} />
                      <Route path="/referrals" element={<Referrals />} />
                      <Route path="/" element={<BridgeExperience />} />
                      <Route path="*" element={<BridgeExperience />} />
                    </Routes>
                  </main>

                  <img
                    src={PoweredByBESC}
                    alt="Powered by BESC"
                    className="powered-by-besc"
                  />

                  <motion.footer
                    className="footer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 0.8 }}
                    role="contentinfo"
                  >
                    © 2025 BESC BRIDGE |{' '}
                    {['Website', 'Swap', 'Twitter'].map((label, i) => {
                      const urls = [
                        'https://bescfinancial.com',
                        'https://bescswap.com',
                        'https://twitter.com/bescllc',
                      ];
                      return (
                        <a
                          key={label}
                          href={urls[i]}
                          className="footer-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                        >
                          {label}
                        </a>
                      );
                    })}
                  </motion.footer>
                </div>
              </div>
            </ReferralContext.Provider>
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </WagmiConfig>
  );
}
